import { useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { combineHoursMinutes, formatMinutesSummary, splitMinutes } from "@/lib/minutes";

export default function MinutesField({
  id,
  name,
  value,
  onChange,
  label = "Reading time",
  help,
  placeholder = "20",
  required = false,
  autoFocus = false,
  error,
}) {
  const generatedId = useId();
  const errorText = Array.isArray(error) ? error[0] : error;
  const fieldId = id || `${name}-${generatedId}`;
  const hoursId = `${fieldId}_hours`;
  const minutesId = `${fieldId}_minutes`;
  const [draft, setDraft] = useState(() => splitMinutes(value));
  const lastLocalValue = useRef(inputValue(value));
  const summary = formatMinutesSummary(value);
  const helperText = summary || help;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const describedBy = errorText ? errorId : helperText ? helpId : undefined;

  useEffect(() => {
    const nextValue = inputValue(value);
    if (nextValue === lastLocalValue.current) return;

    setDraft(splitMinutes(value));
    lastLocalValue.current = nextValue;
  }, [value]);

  const setTotal = (nextHours, nextMinutes) => {
    const nextValue = combineHoursMinutes(nextHours, nextMinutes);
    lastLocalValue.current = inputValue(nextValue);
    setDraft({ hours: nextHours, minutes: nextMinutes });
    onChange?.({ target: { name, value: nextValue } });
  };

  return (
    <div className="space-y-2">
      <input type="hidden" name={name} value={value || ""} readOnly />
      <Label htmlFor={minutesId}>
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor={hoursId} className="text-xs text-muted-foreground">
            Hours
          </Label>
          <Input
            id={hoursId}
            name={`${name}_hours`}
            type="number"
            min="0"
            max="24"
            step="1"
            inputMode="numeric"
            placeholder="0"
            value={draft.hours}
            onChange={(e) => setTotal(e.target.value, draft.minutes)}
            aria-invalid={!!errorText}
            aria-describedby={describedBy}
            className={errorText ? "border-destructive focus-visible:ring-destructive" : ""}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={minutesId} className="text-xs text-muted-foreground">
            Minutes
          </Label>
          <Input
            id={minutesId}
            name={`${name}_minutes`}
            type="number"
            min="0"
            max="1440"
            step="1"
            inputMode="numeric"
            placeholder={placeholder}
            value={draft.minutes}
            onChange={(e) => setTotal(draft.hours, e.target.value)}
            autoFocus={autoFocus}
            required={required && !value}
            aria-invalid={!!errorText}
            aria-describedby={describedBy}
            className={errorText ? "border-destructive focus-visible:ring-destructive" : ""}
          />
        </div>
      </div>
      {errorText ? (
        <p id={errorId} className="text-sm text-destructive">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helpId} className="text-sm text-muted-foreground">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

function inputValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}
